/*
 * Device identity helpers for the PMC monitoring app.
 *
 * The backend binds one physical device to each supervisor account so a
 * stolen password can't be used to capture attendance from a different
 * phone. To recognise a returning device we send two stable signals on
 * every API call:
 *
 *   • A UUID stored in `localStorage` under PMC_DEVICE_ID_KEY. Generated
 *     once on first run and reused forever. Cheap, accurate, but lost if
 *     the user clears browser data.
 *
 *   • A browser fingerprint hash from @fingerprintjs/fingerprintjs (free
 *     OSS, fully client-side, no API key). Survives localStorage clears
 *     because it's derived from canvas/audio/font/timezone/UA. Not 100%
 *     unique (two identical phones can collide) but combined with the
 *     UUID and the per-user scoping on the backend, it's the strongest
 *     practical signal a web app can produce.
 *
 * If localStorage is cleared but the same physical phone is used, the
 * backend silently rebinds the new UUID to the existing approved device
 * row by matching on fingerprint — no admin re-approval needed.
 */
import FingerprintJS from "@fingerprintjs/fingerprintjs";

export const PMC_DEVICE_ID_KEY = "pmc.device.id";
export const PMC_DEVICE_FP_KEY = "pmc.device.fp"; // cached fingerprint to avoid recomputing on every call

let fpPromise = null;
let cachedFingerprint = null;

function generateUuid() {
  // Modern browsers: crypto.randomUUID. Fallback to manual v4 generator.
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Returns the persistent device UUID for this browser, creating one on
 * first call. Safe to call repeatedly — synchronous and very fast.
 */
export function getDeviceId() {
  try {
    let id = localStorage.getItem(PMC_DEVICE_ID_KEY);
    if (!id) {
      id = generateUuid();
      localStorage.setItem(PMC_DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    // localStorage disabled / SSR — fall back to in-memory id (lost on reload).
    if (!getDeviceId._mem) getDeviceId._mem = generateUuid();
    return getDeviceId._mem;
  }
}

/**
 * Allow the caller (e.g. login response handler) to override the local
 * device id when the backend issued a different one (silent rebind case).
 */
export function setDeviceId(id) {
  if (!id) return;
  try { localStorage.setItem(PMC_DEVICE_ID_KEY, id); } catch { /* ignore */ }
}

/**
 * Compute (and cache) the FingerprintJS visitor id. Resolves to a stable
 * 32-character hex hash for the current browser. Returns null on failure
 * so callers can still proceed with just the UUID.
 */
export async function getFingerprint() {
  if (cachedFingerprint) return cachedFingerprint;
  try {
    const cached = localStorage.getItem(PMC_DEVICE_FP_KEY);
    if (cached) {
      cachedFingerprint = cached;
      return cached;
    }
  } catch { /* ignore */ }
  try {
    if (!fpPromise) fpPromise = FingerprintJS.load();
    const fp = await fpPromise;
    const r = await fp.get();
    cachedFingerprint = r.visitorId;
    try { localStorage.setItem(PMC_DEVICE_FP_KEY, cachedFingerprint); } catch { /* ignore */ }
    return cachedFingerprint;
  } catch {
    return null;
  }
}

/**
 * Cheap synchronous accessor — only returns the fingerprint if it has
 * already been computed and cached by `getFingerprint()`. Used by the
 * axios interceptor so we never block requests on fingerprint work.
 */
export function getCachedFingerprint() {
  if (cachedFingerprint) return cachedFingerprint;
  try { return localStorage.getItem(PMC_DEVICE_FP_KEY) || null; } catch { return null; }
}

/**
 * Build the headers attached to every API call.
 */
export function getDeviceHeaders() {
  const headers = {};
  const id = getDeviceId();
  if (id) headers["X-Device-Id"] = id;
  const fp = getCachedFingerprint();
  if (fp) headers["X-Device-Fp"] = fp;
  return headers;
}

/**
 * Build the body fields attached to the login request. This MUST be
 * awaited so the fingerprint is fully computed before the very first
 * login (otherwise the user's first session would be wrongly classified
 * as "first device") — the result is then cached for all later requests.
 */
export async function getLoginDeviceFields() {
  const fp = await getFingerprint();
  return {
    device_id: getDeviceId(),
    device_fp: fp,
    device_user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
  };
}

/**
 * Clear cached fingerprint (useful when forcing a re-compute, eg. after
 * the user updates their browser).
 */
export function resetFingerprintCache() {
  cachedFingerprint = null;
  fpPromise = null;
  try { localStorage.removeItem(PMC_DEVICE_FP_KEY); } catch { /* ignore */ }
}
